import React, {useCallback, useLayoutEffect, useMemo, useState} from "react";
import {useSelector} from "react-redux";
import {CSSTransition, TransitionGroup} from "react-transition-group";
import Chips, {ChipsLayoutTypes, ChipsTypes} from "../../../base/chips/chips";
import {Col} from "reactstrap";
import ReduxStoreRoutes from "../../../../../core/models/static/routes/redux-store-routes";
import VendorAutocomplete from "../../../text-fields/autocomplete/vendor-autocomplete";


const MultiSelectVendorsSearchSection = ({
                                             excludeDefaultVendor = false,
                                             vendors,
                                             setVendors,
                                         }) => {
    const selectedVendor = useSelector(state => state?.[ReduxStoreRoutes.vendorProfile]?.data);
    const [defaultVendor, setDefaultVendor] = useState(null);

    const selectedVendors = useMemo(() => [
        ...(vendors ?? []),
        ...((!!defaultVendor) ? [defaultVendor] : [])
    ], [vendors, defaultVendor])

    /**
     * With each change in the selectedVendor selector and excludeDefaultVendor props:
     * - sets or removes the default vendors' state value based on the values of the dependency.
     */
    useLayoutEffect(() => {
        if (selectedVendor && !excludeDefaultVendor) {
            return setDefaultVendor(selectedVendor);
        }
        setDefaultVendor(null);
    }, [selectedVendor, excludeDefaultVendor])
    /**
     * Removes the given vendor from the list of vendors.
     * @param vendor
     */
    const removeVendor = useCallback((vendor) => {
        setVendors(selectedVendors.filter(e => e.code !== vendor?.code));
    }, [setVendors, selectedVendors]);

    /**
     * Depending on addingDefault:
     * if true => set the default vendor state information.
     * if false => adds the vendor to the list of vendors.
     *
     * @param {any} vendor
     * @param {boolean} addingDefault
     */
    const addVendor = useCallback((vendor, addingDefault = false) => {
        if (selectedVendors.map(e => e.code).includes(vendor.code))
            return;
        if (addingDefault) {
            delete vendor['isMissing'];
            setDefaultVendor(vendor);
        }
        setVendors([...selectedVendors, vendor]);
    }, [setVendors, selectedVendors]);

    /**
     * The chips of the added vendors / default vendor of this component
     * @type {ReactElement[]}
     */
    const chips = useMemo(() => {
        const res = [];
        if (!excludeDefaultVendor) {
            res.push((
                <CSSTransition key={selectedVendor?.code} timeout={0}>
                    <Chips
                        className={'mr-2 mt-2'}
                        layoutType={!defaultVendor ? ChipsLayoutTypes.dashed : ChipsLayoutTypes.solid}
                        type={!defaultVendor ? ChipsTypes.add : ChipsTypes.remove}
                        content={selectedVendor?.code ?? ''}
                        onIconClick={() => !defaultVendor ? addVendor(selectedVendor, true) : removeVendor(selectedVendor)}
                    />
                </CSSTransition>
            ));
        }
        for (const vendor of vendors) {
            res.push((
                <CSSTransition key={vendor.code} timeout={500} classNames="item">
                    <div className=' mr-2 mt-2'>
                        <Chips
                            className={'mr-2 mt-2'}
                            type={ChipsTypes.remove}
                            content={vendor?.code ?? ''}
                            onIconClick={() => removeVendor(vendor)}
                        />
                    </div>
                </CSSTransition>
            ))
        }
        return res;
    }, [excludeDefaultVendor, selectedVendor, defaultVendor, addVendor, removeVendor, vendors]);

    return (
        <>
            <TransitionGroup
                className="row no-gutters align-items-center justify-content-start flex-wrap h-100">
                {chips}
                <CSSTransition timeout={500} classNames="item">
                    <Col xs={12} md={6} lg={5} xl={4} className='mt-2'>
                        <VendorAutocomplete
                            clearOnBlur
                            filterOptions={(options) => options.filter(e => !selectedVendors.map(e => e.code).includes(e.code))}
                            label={'Vendor'}
                            placeholder={'search for vendors...'}
                            renderTags={() => ""}
                            value={null}
                            onChange={(_, vendor) => !!vendor ? addVendor(vendor) : void 0}
                            textFieldProps={{
                                variant: 'filled',
                                InputProps: {disableUnderline: true, sx: {borderRadius: 1}},
                            }}
                        />
                    </Col>
                </CSSTransition>
            </TransitionGroup>
        </>

    );
}

export default MultiSelectVendorsSearchSection;
